/** RGM Studio — Collaborations hub: invites received, shared sessions, sent invites. */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SharedNavigation from "../components/SharedNavigation";
import api from "../lib/api";
import { ArtistChip, StatusPill } from "../components/studio/ArtistChip";
import InviteActions from "../components/studio/InviteActions";

const Section = ({ title, count, children, testId }) => (
  <section className="mb-8" data-testid={testId}>
    <h2 className="text-sm font-bold text-gray-300 mb-3">{title} <span className="text-gray-500">{count}</span></h2>
    {children}
  </section>
);

const Empty = ({ text }) => <p className="text-xs text-gray-500 p-4 rounded-xl border border-white/10 bg-white/[0.02]">{text}</p>;

const Row = ({ children, testId, onClick }) => (
  <li data-testid={testId} onClick={onClick}
    className={`p-3 rounded-xl border border-white/10 bg-white/[0.03] ${onClick ? "cursor-pointer hover:border-fuchsia-400/40 transition-colors" : ""}`}>
    {children}
  </li>
);

export default function StudioCollaborationsPage() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(() => {
    api.get("/studio/collaborations").then((r) => setData(r.data)).catch(() => setData({ invitations: [], accepted: [], sent: [], owned_shared: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);
  const open = (pid) => navigate(`/studio/session/${pid}`);

  return (
    <div className="min-h-screen bg-[#08080c] text-white" data-testid="studio-collabs-page">
      <SharedNavigation activePage="studio" />
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-32">
        <button type="button" onClick={() => navigate("/studio")} className="text-xs text-gray-400 mb-3" data-testid="collabs-back-btn">← Studio</button>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-1">Collaborations</h1>
        <p className="text-sm text-gray-400 mb-8">Record together with RGM artists. Each artist keeps control of their own takes.</p>
        {!data ? <p className="text-sm text-gray-500" data-testid="collabs-loading">Loading…</p> : (
          <>
            <Section title="Invitations" count={data.invitations.length} testId="collabs-invitations">
              {data.invitations.length === 0 ? <Empty text="No pending invitations." /> : (
                <ul className="space-y-2">
                  {data.invitations.map((r) => (
                    <Row key={r.invite_id} testId={`collabs-invite-${r.project.project_id}`}>
                      <p className="text-sm font-bold">{r.project.title}</p>
                      <div className="mt-1 mb-1"><ArtistChip profile={r.project.owner} sub="invited you" /></div>
                      <InviteActions projectId={r.project.project_id} inviteId={r.invite_id} onDone={load} />
                    </Row>
                  ))}
                </ul>
              )}
            </Section>
            <Section title="Shared with me" count={data.accepted.length} testId="collabs-accepted">
              {data.accepted.length === 0 ? <Empty text="Accepted sessions show up here and in your Studio projects." /> : (
                <ul className="space-y-2">
                  {data.accepted.map((r) => (
                    <Row key={r.invite_id} testId={`collabs-shared-${r.project.project_id}`} onClick={() => open(r.project.project_id)}>
                      <div className="flex items-center gap-3">
                        <span className="flex-1 min-w-0">
                          <p className="text-sm font-bold truncate">{r.project.title}</p>
                          <ArtistChip profile={r.project.owner} sub="· owner" />
                        </span>
                        <span className="text-xs text-fuchsia-300 font-bold">Open →</span>
                      </div>
                    </Row>
                  ))}
                </ul>
              )}
            </Section>
            <Section title="My shared projects" count={data.owned_shared.length} testId="collabs-owned">
              {data.owned_shared.length === 0 ? <Empty text="Invite artists from inside any of your Studio sessions." /> : (
                <ul className="space-y-2">
                  {data.owned_shared.map((p) => (
                    <Row key={p.project_id} testId={`collabs-owned-${p.project_id}`} onClick={() => open(p.project_id)}>
                      <div className="flex items-center gap-3">
                        <p className="flex-1 text-sm font-bold truncate">{p.title}</p>
                        <span className="text-xs text-fuchsia-300 font-bold">Open →</span>
                      </div>
                    </Row>
                  ))}
                </ul>
              )}
            </Section>
            <Section title="Invites I sent" count={data.sent.length} testId="collabs-sent">
              {data.sent.length === 0 ? <Empty text="No invites sent yet." /> : (
                <ul className="space-y-2">
                  {data.sent.map((r) => (
                    <Row key={`${r.project.project_id}-${r.user_id}`} testId={`collabs-sent-${r.project.project_id}-${r.user_id}`}>
                      <div className="flex items-center gap-2">
                        <span className="flex-1 min-w-0"><ArtistChip profile={r.profile} sub={`· ${r.project.title}`} /></span>
                        <StatusPill status={r.status} testId={`collabs-sent-status-${r.project.project_id}-${r.user_id}`} />
                      </div>
                    </Row>
                  ))}
                </ul>
              )}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
